import { useEffect, useId, useRef, useState } from "react";
import { useSession } from "@/session/useSession";
import { AppError, uploadFile, type UploadOwner } from "@/api/runtime";
import type { FileUploadOutput } from "@/api/tools";
import { MISSING, Banner, fmtDate, Pill, RequiredMark, Table } from "@/ui";

const PDF = "application/pdf";
const DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const DOCUMENT_TYPES = [PDF, DOCX];
export const FILE_TYPES = [PDF, DOCX, "image/png", "text/csv"];
const REASONS: Record<string, string> = {
  too_large: "The file exceeds the upload limit. Choose a smaller file.",
  type_disallowed: "This file type is not accepted. Choose one of the listed types.",
  infected: "The file failed its safety scan. Choose a clean copy.",
  scan_unavailable_timeout: "The safety scan could not finish. Try uploading again.",
};
export const fileSize = (bytes: number) => bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KiB` : `${(bytes / 1024 / 1024).toFixed(1)} MiB`;

export type FilePhase = "idle" | "uploading" | "scanning" | "ready" | "rejected" | "failed";

/** The server alone decides type, scan result, seat and version. `ready` means ready to attach, not yet downloadable. */
export function FileField({ label, owner, onChange, onBusy, onPhase, required = false, disabled = false, disabledReason, maxUploadBytes, accept = FILE_TYPES, compactIdle = false }: {
  label: string; owner: UploadOwner; onChange: (file: FileUploadOutput | null) => void;
  onPhase?: ((phase: FilePhase) => void) | undefined;
  onBusy?: ((busy: boolean) => void) | undefined; required?: boolean; disabled?: boolean;
  disabledReason?: string | undefined; maxUploadBytes?: number | undefined; accept?: string[]; compactIdle?: boolean;
}) {
  const { maxUploadBytes: sessionLimit } = useSession();
  maxUploadBytes ??= sessionLimit;
  const id = `file-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [phase, setPhase] = useState<FilePhase>("idle");
  const [result, setResult] = useState<FileUploadOutput | null>(null);
  const [error, setError] = useState<AppError | Error | null>(null);
  const [rejection, setRejection] = useState("");
  const selected = useRef<File | null>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => { request.current?.abort(); onBusy?.(false); }, [onBusy]);
  useEffect(() => { onPhase?.(phase); }, [onPhase, phase]);
  async function upload(file: File) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    selected.current = file;
    onChange(null);
    setResult(null);
    setError(null);
    setRejection("");
    if (maxUploadBytes !== undefined && file.size > maxUploadBytes) {
      setRejection(REASONS.too_large!); setPhase("rejected"); onBusy?.(false); return;
    }
    setPhase("uploading"); onBusy?.(true);
    try {
      const response = await uploadFile(file, owner, { signal: controller.signal, onPhase: (next) => { if (!controller.signal.aborted) setPhase(next); } });
      if (controller.signal.aborted) return;
      setResult(response);
      if (response.status === "scanned_clean" || response.status === "available") { setPhase("ready"); onChange(response); }
      else { setPhase("rejected"); setRejection(REASONS[response.reject_reason ?? ""] ?? "The file is not ready to attach. Try again."); }
    } catch (e) {
      if (controller.signal.aborted) return;
      const err = e instanceof Error ? e : new Error("Upload failed");
      if (err instanceof AppError && (err.rejectReason || [413, 415].includes(err.status))) {
        setPhase("rejected"); setRejection(REASONS[err.rejectReason ?? ""] ?? err.message);
      } else { setPhase("failed"); setError(err); }
    } finally { if (!controller.signal.aborted) onBusy?.(false); }
  }
  const busy = phase === "uploading" || phase === "scanning";
  return <div className={"field file-field" + (compactIdle && phase === "idle" ? " file-field--compact" : "")} data-state={phase} aria-busy={busy}>
    <div className="field__label"><label htmlFor={id}>{label}</label>{required && <RequiredMark />}</div>
    <input id={id} type="file" accept={accept.join(",")} disabled={disabled || busy} aria-required={required} aria-describedby={`${id}-hint ${id}-status`} aria-invalid={phase === "rejected" || phase === "failed"} onChange={(e) => { const file = e.target.files?.[0]; if (file) void upload(file); }} />
    <p id={`${id}-hint`} className="field__hint">{accept.map((t) => t === PDF ? "PDF" : t === DOCX ? "DOCX" : t === "image/png" ? "PNG" : "CSV").join(" · ")}{maxUploadBytes !== undefined ? ` · up to ${fileSize(maxUploadBytes)}` : " · size and type checked on upload"}. {disabledReason}</p>
    <div id={`${id}-status`} role="status" aria-live="polite" className="file-field__status">
      {phase === "idle" && "No file selected."}
      {phase === "uploading" && "Uploading…"}
      {phase === "scanning" && "Scanning… waiting for the safety check."}
      {phase === "rejected" && <span className="field__error">Rejected — {rejection}</span>}
      {phase === "failed" && "Upload failed. Your record has not changed."}
      {phase === "ready" && result && <><Pill tone="ok">Ready to attach · v{result.version}</Pill> {fileSize(result.size_bytes)}{result.duplicate_of ? " · existing identical version" : ""}<span className="file-field__hash mono">SHA-256 {result.sha256}</span></>}
    </div>
    <Banner error={error} />
    {(phase === "failed" || (phase === "rejected" && result?.reject_reason === "scan_unavailable_timeout")) && <button type="button" className="btn btn--sm" disabled={disabled} onClick={() => selected.current && void upload(selected.current)}>Retry upload</button>}
  </div>;
}

export interface FileVersion {
  file_id: string; version?: number | null | undefined; uploaded_at?: string | undefined; uploaded_by_name?: string | undefined;
  size_bytes?: number | undefined; sha256?: string | undefined; pinned?: boolean | undefined;
}

/** Only pass the list the current actor's projection returned. A missing metadata field stays missing. */
export function FileList({ files, label = "Files" }: { files: FileVersion[]; label?: string }) {
  if (!files.length) return <p className="muted">Not on file.</p>;
  return <Table label={label} head={<tr><th scope="col">Version</th><th scope="col">Uploaded</th><th scope="col">Size / SHA-256</th><th scope="col">Action</th></tr>}>
    {files.map((file) => <tr key={file.file_id}>
      <td className="nowrap">{file.version != null ? `v${file.version}` : "On file"}{file.pinned && <div><Pill tone="info">Pinned for review</Pill></div>}</td>
      <td>{file.uploaded_at ? fmtDate(file.uploaded_at, true) : MISSING}{file.uploaded_by_name && <div>by {file.uploaded_by_name}</div>}</td>
      <td>{file.size_bytes != null ? fileSize(file.size_bytes) : MISSING}{file.sha256 && <div className="mono" title={file.sha256}>{file.sha256.slice(0, 12)}</div>}</td>
      <td><a className="btn btn--sm" href={`/api/files/${encodeURIComponent(file.file_id)}`} download aria-label={`Download ${label.toLowerCase()}${file.version != null ? ` v${file.version}` : ""}`}>Download</a></td>
    </tr>)}
  </Table>;
}
